import { Field as FormField } from '../../libs/gs-component-form';
import { Content, Footer, Header, RouterContext, TripleAction } from '../../libs/gs-component-right-rail';
import { VanityClient } from '../../libs/wws-api-client';
import { Field as FormikField, Formik } from 'formik';
import Honeybadger from 'honeybadger-js';
import React, { useContext, useState } from 'react';
import * as Yup from 'yup';
import { DataProxy } from 'apollo-cache';

import { useUrlMutation } from '../../generated/graphql';
import styles from './editUrl.scss';
import CVAlert from '../CVAlert';
import UpgradeURLCallout from '../PurchaseDomainModal/UpgradeURLCallout/UpgradeURLCallout';
import { useClient } from '../../apollo/ApolloClientsProvider';
import { WeddingWebsiteQuery, WeddingWebsiteQueryDocument } from '../../generated/graphql';

interface InitialValues {
  url: string;
}

interface Props {
  params: {
    vanityUrl?: string;
  };
}

const message = 'Must be at least 2 characters in length and not include any special characters.';

export const updateUrlSuccessCallback = (cache: DataProxy, vanityUrl: string) => {
  const { weddingWebsite, ...rest } = cache.readQuery<WeddingWebsiteQuery>({ query: WeddingWebsiteQueryDocument })!;
  const updatedWeddingWebsite = { ...weddingWebsite, vanityUrl };
  cache.writeQuery({
    query: WeddingWebsiteQueryDocument,
    data: { weddingWebsite: updatedWeddingWebsite, ...rest },
  });
};

const validationSchema = Yup.object().shape({
  url: Yup.string()
    .required(message)
    .min(2, message)
    // any alphanumeric character, underscore, or hyphen
    .matches(/^[\w-]*$/i, message),
});

const vanityClient = new VanityClient();

export const EditUrl: React.FC<Props> = ({ params: { vanityUrl } }) => {
  const { goBack } = useContext(RouterContext);
  const client = useClient();
  const [updateUrl, { loading }] = useUrlMutation();
  const [erroring, setErroring] = useState(false);

  const onSave = async (values: InitialValues): Promise<void> => {
    try {
      const response = await updateUrl({
        variables: {
          attributes: {
            vanityUrl: values.url,
          },
        },
      });
      const transformedUrl = response.data?.updateWeddingWebsite.vanityUrl;
      updateUrlSuccessCallback(client.cache, `${transformedUrl}`);

      goBack();
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error updating url',
        context: {
          error,
        },
      });
      setErroring(true);
    }
  };

  const initialValues: InitialValues = {
    url: vanityUrl || '',
  };

  const validateAvailable = (value: string | undefined): Promise<string | void> | undefined => {
    if (value && value.length >= 2) {
      return vanityClient
        .isUrlAvailable(value)
        .then(({ data }) => {
          if (data.available === false) {
            // eslint-disable-next-line
            throw 'URL not available';
          }
        })
        .catch(e => {
          return e;
        });
    }

    // eslint-disable-next-line
    return;
  };

  return (
    <>
      <Header leftButtonText="Back" leftButtonOnClick={goBack}>
        Edit URL
      </Header>
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSave}>
        {({ submitForm, errors }): JSX.Element => (
          <>
            <Content>
              <CVAlert erroring={erroring} onTimeout={() => setErroring(false)} />
              <div className={styles.container}>
                <FormikField
                  component={FormField}
                  name="url"
                  label="theknot.com"
                  validate={validateAvailable}
                  subText={!errors.url && 'URL available! Save to complete changes.'}
                />
              </div>
              <UpgradeURLCallout />
            </Content>
            <Footer>
              <TripleAction primaryOnClick={submitForm} primaryText="Save" primaryDisabled={loading} />
            </Footer>
          </>
        )}
      </Formik>
    </>
  );
};
